import { StrictMode } from "react";

import AuthRouterProvider from "@/components/auth/AuthRouterProvider";
import { KeycloakProvider } from "@/components/keycloak/KeycloakProvider";
import { env } from "@/env";
import { queryClient, router } from "@/router";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRoot } from "react-dom/client";

import { DUMMY_ERROR_PREFIX, SENTRY_ERROR_TYPE, SENTRY_SEVERITY } from "@workspace/schemas/errors";
import { Toaster } from "@workspace/ui/components/sonner";
import "@workspace/ui/globals.css";

if (env.VITE_ENVIRONMENT !== "local")
  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    environment: env.VITE_ENVIRONMENT,
    release: env.VITE_SENTRY_RELEASE,
    integrations: [
      // https://docs.sentry.io/platforms/javascript/guides/react/features/tanstack-router/
      // Create pageload/navigation transactions to collect meaningful PERFORMANCE data about the health our page loads and associated requests (not relevant for error tracking)
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      // https://docs.sentry.io/platforms/javascript/guides/react/session-replay/configuration/
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        // Record up to a minute of events prior to the error and continue recording for 4 minutes after the error.
        maxReplayDuration: 300000, // 5 minutes in milliseconds
      }),
    ],
    // Track all errors (default is 1.0)
    sampleRate: 1.0,
    // Track 20% of transactions
    tracesSampleRate: 0.2,
    // The sample rate for replays that begin recording immediately and last the entirety of the user's session --> disabled
    replaysSessionSampleRate: 0.0,
    // Always record replays for errors --> maximum 5 minutes as configured in the replayIntegration integration
    replaysOnErrorSampleRate: 1.0,
    // Add default tags for uncaught exceptions (events without custom tags)
    beforeSend(event) {
      const isDummy = event.exception?.values?.some((v) => v.value?.startsWith(DUMMY_ERROR_PREFIX));
      const isInfoMessage = event.level === "info";

      if (isDummy) {
        event.tags = { ...event.tags, dummy: "true" };
      }
      if (!isInfoMessage && !event.tags?.severity) {
        event.tags = { ...event.tags, severity: SENTRY_SEVERITY.UNKNOWN };
      }
      if (!isInfoMessage && !event.tags?.errorType) {
        event.tags = { ...event.tags, errorType: SENTRY_ERROR_TYPE.UNKNOWN };
      }
      if (!event.tags?.envFilter) {
        event.tags = { ...event.tags, envFilter: env.VITE_ENVIRONMENT };
      }
      return event;
    },
  });

// Recover stale tabs after a deploy: when a lazily-imported chunk 404s because a newer build
// replaced its hashed filename, Vite fires `vite:preloadError`. A full reload pulls fresh
// index.html + chunk hashes, landing the tab on the latest build. The sessionStorage guard makes
// this a one-shot reload so a genuinely-missing chunk can't loop — a repeat within the window
// falls through to the route error boundary, whose button then offers a manual hard reload.
// https://vite.dev/guide/build#load-error-handling
window.addEventListener("vite:preloadError", (event) => {
  const RELOAD_GUARD_KEY = "vite:preloadError:lastReload";
  const RELOAD_GUARD_MS = 10_000;
  const lastReload = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
  if (Date.now() - lastReload < RELOAD_GUARD_MS) return; // already reloaded very recently — let it surface
  sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  event.preventDefault(); // handled — don't let Vite rethrow
  window.location.reload();
});

const rootElement = document.getElementById("root")!;

if (!rootElement.innerHTML) {
  const root = createRoot(rootElement, {
    // Errors NOT caught by any Error Boundary (safety net for errors outside TanStack Router, e.g. in KeycloakProvider)
    onUncaughtError: Sentry.reactErrorHandler(),
    // Recoverable errors (hydration mismatches, rare in SPA)
    onRecoverableError: Sentry.reactErrorHandler(),
  });
  root.render(
    <KeycloakProvider>
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <AuthRouterProvider />
          <Toaster />
        </QueryClientProvider>
      </StrictMode>
    </KeycloakProvider>,
  );
}
